<template>
    <div class="layout-settings-block rounded border border-ui-lines relative">
        <div class="w-full border-b border-ui-lines px-3 lg:px-4 h-16">
            <div class="flex items-center justify-between gap-5 lg:gap-10 h-full">
                <div class="flex-grow h-full">
                    <LayoutSettingTabs
                        v-model="activeTab"
                    />
                </div>
                <div>
                    <i
                        class="vc-icon-close cursor-pointer icon-base text-secondary hover:text-primary-actions"
                        @click="onCancel"
                    />
                </div>
            </div>
        </div>
        <div class="layout-settings-block__content px-3 lg:px-6 py-8 lg:py-10">
            <Component
                :is="activeTabComponent"
                v-model="layoutLocalModel"
            />
        </div>
        <div class="layout-settings-block__footer px-3 lg:px-6">
            <div class="flex justify-between border-t border-ui-lines py-3 lg:py-6">
                <div>
                    <RedesignBaseButton
                        v-if="layoutLocalModel.LayoutID"
                        :loading="loading"
                        icon="vc-icon-recycle-bin"
                        color="destructive"
                        @click="onDelete"
                    >
                        {{ $t('common.deleteLayout') }}
                    </RedesignBaseButton>
                </div>
                <div class="flex items-center gap-2 xl:gap-3">
                    <RedesignBaseButton
                        :loading="loading"
                        type="outline"
                        color="secondary"
                        @click="onCancel"
                    >
                        {{ $t('common.cancel') }}
                    </RedesignBaseButton>
                    <RedesignBaseButton
                        :loading="loading"
                        :disabled="!layoutLocalModel.LayoutName"
                        type="default"
                        color="primary"
                        @click="onSave"
                    >
                        {{ $t('common.saveLayout') }}
                    </RedesignBaseButton>
                </div>
            </div>
        </div>
        <div
            v-if="loading"
            class="loader absolute inset-0 flex-center bg-black/10 rounded"
        >
            <Stretch
                :size="40"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import LayoutSettingTabs from '@/components/RedesignComponents/Layout/Common/LayoutSettingTabs.vue'
import LayoutGeneralSettings from '@/components/RedesignComponents/Layout/Common/LayoutGeneralSettings.vue'
import { ILayoutItem } from '@/types/dashboard'
import RedesignBaseButton from '@/components/RedesignBaseComponents/Buttons/RedesignBaseButton.vue'
import LayoutTimeSoundsSettings from '@/components/RedesignComponents/Layout/Common/LayoutTimeSoundsSettings.vue'
import LayoutRefreshIntervalSettings
    from '@/components/RedesignComponents/Layout/Common/LayoutRefreshIntervalSettings.vue'
import LayoutAdvancedSettings from '@/components/RedesignComponents/Layout/Common/LayoutAdvancedSettings.vue'
import LayoutStatusAlertsSettings from '@/components/RedesignComponents/Layout/Common/LayoutStatusAlertsSettings.vue'
import {
    LAYOUT_SETTINGS_ADVANCED_TAB,
    LAYOUT_SETTINGS_GENERAL_TAB,
    LAYOUT_SETTINGS_INTERVALS_TAB,
    LAYOUT_SETTINGS_TIMERS_TAB,
    LAYOUT_SETTINGS_STATUS_ALERTS_TAB
} from '@/enum/layout'
import Stretch from '@/components/RedesignBaseComponents/Loader/Stretch.vue'

const props = defineProps<{
    loading?: boolean
    currentLayout: ILayoutItem
}>()
const emit = defineEmits([ 'save', 'update:currentLayout', 'cancel', 'delete' ])

const layoutLocalModel = ref()

const activeTab = ref('general')

const activeTabComponent = computed(() => {
    return {
        [LAYOUT_SETTINGS_GENERAL_TAB]: LayoutGeneralSettings,
        [LAYOUT_SETTINGS_TIMERS_TAB]: LayoutTimeSoundsSettings,
        [LAYOUT_SETTINGS_INTERVALS_TAB]: LayoutRefreshIntervalSettings,
        [LAYOUT_SETTINGS_ADVANCED_TAB]: LayoutAdvancedSettings,
        [LAYOUT_SETTINGS_STATUS_ALERTS_TAB]: LayoutStatusAlertsSettings
    }[activeTab.value]
})

function onCancel () {
    emit('cancel')
}

function onSave () {
    emit('save', { ...layoutLocalModel.value })
}

function onDelete () {
    emit('delete', { ...layoutLocalModel.value })
}

watch(
    () => props.currentLayout,
    (layout) => {
        layoutLocalModel.value = { ...layout }
    },
    {
        immediate: true
    }
)
watch(
    layoutLocalModel,
    (layoutData) => {
        emit('update:currentLayout', layoutData)
    },
    {
        deep: true
    }
)
</script>

